// src/components/surveys/builder/QuestionCard.tsx
import React from 'react';
import {
  GripVertical,
  Trash2,
  Copy,
  ChevronUp,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
} from 'lucide-react';
import { SurveyQuestion, QuestionType } from '../../../types/survey.types';
import { QuestionRenderer } from '../questions';

interface QuestionCardProps {
  question: SurveyQuestion;
  isSelected: boolean;
  isDragging?: boolean;
  validationErrors?: string[];
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleRequired: () => void;
  isFirst: boolean;
  isLast: boolean;
  dragHandleProps?: any;
}

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  yes_no: 'Yes/No',
  multiple_choice: 'Multiple Choice',
  multi_select: 'Multi-Select',
  text: 'Text',
  number: 'Number',
  rating_scale: 'Rating Scale',
  dropdown: 'Dropdown',
  date: 'Date',
  phone: 'Phone',
  email: 'Email',
};

const QUESTION_TYPE_COLORS: Record<QuestionType, string> = {
  yes_no: 'bg-green-100 text-green-700',
  multiple_choice: 'bg-blue-100 text-blue-700',
  multi_select: 'bg-purple-100 text-purple-700',
  text: 'bg-gray-100 text-gray-700',
  number: 'bg-orange-100 text-orange-700',
  rating_scale: 'bg-yellow-100 text-yellow-700',
  dropdown: 'bg-cyan-100 text-cyan-700',
  date: 'bg-pink-100 text-pink-700',
  phone: 'bg-indigo-100 text-indigo-700',
  email: 'bg-teal-100 text-teal-700',
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  isSelected,
  isDragging = false,
  validationErrors = [],
  onSelect,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onToggleRequired,
  isFirst,
  isLast,
  dragHandleProps,
}) => {
  const hasErrors = validationErrors.length > 0;

  return (
    <div
      className={`bg-white rounded-lg border-2 transition-all ${
        isDragging
          ? 'opacity-50 shadow-lg'
          : isSelected
          ? 'border-indigo-500 shadow-md'
          : hasErrors
          ? 'border-red-300'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="h-5 w-5" />
          </div>

          {/* Question number */}
          <span className="font-medium text-gray-500">Q{question.order + 1}</span>

          {/* Question type badge */}
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              QUESTION_TYPE_COLORS[question.type]
            }`}
          >
            {QUESTION_TYPE_LABELS[question.type]}
          </span>

          {/* Required badge */}
          {question.required && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
              Required
            </span>
          )}

          {/* Error indicator */}
          {hasErrors && (
            <div className="flex items-center text-red-500" title={validationErrors.join('\n')}>
              <AlertCircle className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Move buttons */}
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className={`p-1.5 rounded ${
              isFirst
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className={`p-1.5 rounded ${
              isLast
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>

          {/* Required toggle */}
          <button
            onClick={onToggleRequired}
            className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            title={question.required ? 'Make optional' : 'Make required'}
          >
            {question.required ? (
              <ToggleRight className="h-4 w-4 text-indigo-600" />
            ) : (
              <ToggleLeft className="h-4 w-4" />
            )}
          </button>

          {/* Duplicate */}
          <button
            onClick={onDuplicate}
            className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>

          {/* Delete */}
          <button
            onClick={onDelete}
            className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Question content */}
      <div className="p-4">
        {/* Question text */}
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {question.text || (
            <span className="text-gray-400 italic">Enter question text...</span>
          )}
        </h3>

        {/* Description */}
        {question.description && (
          <p className="text-sm text-gray-500 mb-3">{question.description}</p>
        )}

        {/* Question preview */}
        <div className="mt-4">
          <QuestionRenderer question={question} mode="edit" disabled />
        </div>
      </div>

      {/* Validation errors */}
      {hasErrors && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100">
          {validationErrors.map((error, index) => (
            <p key={index} className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
